import { FileText, Shield, Zap, Lock, Cloud, Terminal, Cpu } from 'lucide-react';

export const INTEL_CATEGORIES = [
  { id: "all", label: "ALL" },
  { id: "strategy", label: "STRATEGY" },
  { id: "governance", label: "GOVERNANCE" },
  { id: "ai", label: "AI" },
  { id: "scalability", label: "SCALABILITY" },
  { id: "business-improvement", label: "BUSINESS IMPROVEMENT" },
  { id: "digital-transformation", label: "DIGITAL TRANSFORMATION" },
  { id: "computer-vision", label: "COMPUTER VISION" },
  { id: "generative-ai", label: "GENERATIVE AI" },
  { id: "predictive-analytics", label: "PREDICTIVE ANALYTICS" },
  { id: "nlp", label: "NLP" },
  { id: "optimization", label: "OPTIMIZATION" },
  { id: "project-management", label: "PROJECT MANAGEMENT" },
  { id: "change-management", label: "CHANGE MANAGEMENT" }
];

export const INTEL_REPORTS = [
  {
    id: "sovereignty-compliance-architecture",
    ref_id: "OPS_DIRECTIVE_2026",
    title: "PROTOCOL: SOVEREIGNTY, COMPLIANCE & ARCHITECTURE",
    category: "governance",
    sector: "PROTOCOL / GOVERNANCE",
    sensitivity: "PUBLIC",
    author_node: "SYDNEY",
    ppt_coverage: ["process", "technology"],
    desc: "Operational directives on Australian Data Sovereignty, Compliance Frameworks, and DD Consulting's dual-track engagement model.",
    date: "2026-01-20",
    read_time: "6 MIN READ",
    diagram_type: "protocol",
    pinned: true,
    isFAQ: true,
    tags: ["#EnterpriseModernisation", "#Sovereignty", "#Compliance"],
    // Impact Radius Data
    impact_radius: {
      metric1: "SOVEREIGNTY: ENFORCED",
      metric2: "COMPLIANCE: APRA + APP",
      metric3: "DUAL TRACK: READY"
    },
    // FAQ Content for Schema
    faq_sections: [
      {
        question: "How does DD Consulting handle Australian Data Sovereignty?",
        answer: "We adhere to a strict \"Australia-First\" policy for regulated sectors. All critical infrastructure is deployed within the AWS Sydney Region (ap-southeast-2). We implement IAM protocols ensuring data belonging to Australian entities (Legal, Health, Government) never leaves Australian jurisdiction, satisfying APRA CPS 234 and federal privacy obligations."
      },
      {
        question: "Can you retrofit legacy systems for modern compliance?",
        answer: "Yes. Our \"Enterprise Modernisation\" division specializes in remediating technical debt. We re-architect legacy platforms to align with the Australian Privacy Principles (APP) and Aged Care Quality Standards. We engineer \"safety nets\" around your core systems to secure compliance first."
      },
      {
        question: "Do you work with Startups or Enterprise?",
        answer: "We operate two distinct tracks: Venture Product Engineering for Pre-Seed to Series A startups focusing on speed and MVP architecture, and Enterprise Modernisation for established firms focusing on security, uptime, and governance."
      }
    ],
    // Case Study Content
    executive_abstract: "REF_ID: OPS_DIRECTIVE_2026 // STATUS: PUBLIC. This protocol document outlines DD Consulting's operational framework for Australian Data Sovereignty, Compliance Retrofitting, and our dual-track engagement model serving both high-growth startups and established enterprises.",
    business_friction: "REGULATORY COMPLEXITY. Australian organizations face an increasingly complex compliance landscape. Data sovereignty requirements, privacy obligations (APP), financial services regulations (APRA CPS 234), and sector-specific standards create operational friction that impedes digital transformation initiatives.",
    intervention: "COMPLIANCE-FIRST ARCHITECTURE. We embed compliance into the architecture layer—not as an afterthought. Our \"Australia-First\" data residency policy ensures regulated data never leaves Australian jurisdiction. For legacy systems, we engineer protective layers that enable modernization without compromising existing compliance postures."
  },
  {
    id: "immersive-architecture",
    ref_id: "CASE_01",
    title: "THE IMMERSIVE ARCHITECTURE",
    category: "strategy",
    sector: "STRATEGY / VENTURE",
    sensitivity: "PUBLIC_RELEASE",
    author_node: "SYDNEY",
    ppt_coverage: ["people", "process", "technology"],
    desc: "Transitioning from an 'Attention Economy' to an 'Intention Economy' to secure Series A funding.",
    date: "2025-01-15",
    read_time: "10 MIN READ",
    diagram_type: "platform-evolution",
    // Impact Radius Data
    impact_radius: {
      metric1: "SERIES A: SECURED",
      metric2: "UPTIME: 99.9%",
      metric3: "TIMELINE: 24-MONTH"
    },
    // Case Study Content
    executive_abstract: "FROM ATTENTION TO INTENTION. The client had a successful gaming platform but lacked the architectural \"moat\" required to secure Series A funding. Investors saw a game; we needed to show them an economy.",
    business_friction: "THE INNOVATION GAP. The business model was stuck in the \"Attention Economy\" (monetizing time), while the market demanded an \"Intention Economy\" (monetizing assets). The existing infrastructure lacked the \"Connective Tissue\" to link gaming and live streaming.",
    intervention: "TRUST THROUGH DATA INTEGRITY. We defined a roadmap that rejected the \"Data Sale\" model. We architected a dedicated AWS environment to handle complex digital identities, proving to investors the tech was proprietary."
  },
  {
    id: "data-residency-framework",
    ref_id: "CASE_02",
    title: "THE DATA RESIDENCY FRAMEWORK",
    category: "governance",
    sector: "GOVERNANCE / AGED CARE",
    sensitivity: "PUBLIC_RELEASE",
    author_node: "NEW_JERSEY",
    ppt_coverage: ["people", "process", "technology"],
    desc: "Moving from a 'Spreadsheet Shadow Economy' to a Golden Record single source of truth.",
    date: "2025-01-20",
    read_time: "8 MIN READ",
    diagram_type: "data-governance",
    // Impact Radius Data
    impact_radius: {
      metric1: "RISK: ELIMINATED",
      metric2: "EFFICIENCY: 40% GAIN",
      metric3: "AUDIT: READY"
    },
    // Case Study Content
    executive_abstract: "GOVERNANCE BEFORE GROWTH. In regulated industries, bad data isn't just annoying—it's a liability. We moved the client from a fragmented \"Spreadsheet Shadow Economy\" to a single source of truth.",
    business_friction: "THE \"EXCEL\" PATCH. Critical care data was trapped in isolated pockets. Staff were using manual workarounds—including the \"Mandarin Block,\" where coordinators manually filtered rosters to match language skills.",
    intervention: "THE GOLDEN RECORD. We audited the \"Process Debt\" first. We integrated the siloed systems (Carelink, Halo, Finance) to ensure that a clinical risk in one system was immediately visible in the rostering system.",
    // Execution Protocol
    execution_protocol: {
      "Methodology": "Agile / Lean Six Sigma",
      "Resource_Model": "Fractional CTO + Data Governance Lead",
      "Governance_Std": "Aged Care Quality Standards",
      "Sprint_Cycle": "2-Week Iterations",
      "Compliance_Framework": "ISO 27001 + NDIS"
    }
  },
  {
    id: "integrity-moat",
    ref_id: "CASE_03",
    title: "THE INTEGRITY MOAT",
    category: "ai",
    sector: "AI / SPORTS TECH",
    sensitivity: "PUBLIC_RELEASE",
    author_node: "SYDNEY",
    ppt_coverage: ["process", "technology"],
    desc: "Monetizing data accuracy: How an audit trail unlocked a premium market tier.",
    date: "2025-02-01",
    read_time: "7 MIN READ",
    diagram_type: "data-accuracy",
    // Impact Radius Data
    impact_radius: {
      metric1: "ACCURACY: 95%",
      metric2: "EFFICIENCY: 25 HRS/WK",
      metric3: "REVENUE: +$50K/CLIENT"
    },
    // Case Study Content
    executive_abstract: "MONETIZING ACCURACY. In the betting market, data latency costs millions. We transitioned the client from a \"Black Box\" AI model to an \"Auditable\" data feed, unlocking a premium market tier.",
    business_friction: "THE 52% ERROR RATE. The client's existing data feed suffered from massive inaccuracy during live events. This forced sportsbooks to close betting lines early to manage risk, capping the client's revenue potential.",
    intervention: "TRUST-AS-A-SERVICE. We deployed a proprietary AI Auditing Module. This system reduced the need for manual post-fight analysis and created a \"Gold Standard\" data tier, allowing the client to enter the regulated betting market."
  },
  {
    id: "infrastructure-unlock",
    ref_id: "CASE_04",
    title: "THE INFRASTRUCTURE UNLOCK",
    category: "project-management",
    sector: "SCALABILITY / AGENCY",
    sensitivity: "PUBLIC_RELEASE",
    author_node: "SYDNEY",
    ppt_coverage: ["people", "process", "technology"],
    desc: "Overcoming legacy monolith limitations to unlock infinite scalability for global digital agencies.",
    date: "2025-02-10",
    read_time: "6 MIN READ",
    diagram_type: "infrastructure-scaling",
    // Impact Radius Data
    impact_radius: {
      metric1: "REVENUE: UNLOCKED",
      metric2: "DEPLOYMENT: AUTOMATED",
      metric3: "SCALE: INFINITE"
    },
    // Case Study Content
    executive_abstract: "COMMERCIAL ARCHITECTURE. The sales team was selling Ferraris, but the infrastructure was a gravel road. We aligned the tech stack with the commercial ambition.",
    business_friction: "THE SLA BLOCKER. The client was losing high-value contracts in Education and Legal because their \"static\" infrastructure could not support Enterprise SLAs. They were trapped in the low-margin startup market.",
    intervention: "DYNAMIC ORCHESTRATION. We migrated the business to a modular, automated architecture. We unlocked the ability to deploy \"Add-On\" features (like AI) without manual configuration, enabling six-figure contract wins."
  },
  {
    id: "inventory-intelligence",
    ref_id: "CASE_05",
    title: "THE INVENTORY INTELLIGENCE",
    category: "business-improvement",
    sector: "BUSINESS IMPROVEMENT / RETAIL",
    sensitivity: "PUBLIC_RELEASE",
    author_node: "VIETNAM",
    ppt_coverage: ["process", "technology"],
    desc: "Avoiding the 'Build Trap': API-first integration for Just-in-Time global forecasting.",
    date: "2025-02-20",
    read_time: "5 MIN READ",
    diagram_type: "build-vs-buy",
    // Impact Radius Data
    impact_radius: {
      metric1: "CAPEX: SAVED",
      metric2: "SPEED: IMMEDIATE",
      metric3: "LOGIC: BEST-IN-CLASS"
    },
    // Case Study Content
    executive_abstract: "MEASURE TWICE, CUT ONCE. The client wanted to build a custom forecasting engine from scratch. We stopped them. Why build a generic tool when you can integrate a best-in-class solver?",
    business_friction: "THE \"BUILD\" TRAP. The client was about to commit massive capital to build a custom module. This would have created years of \"Technical Debt\"—forcing them to maintain complex mathematical algorithms instead of selling products.",
    intervention: "API-FIRST ORCHESTRATION. We deployed a \"Build vs. Buy\" strategy. We treated their existing platform as the \"Source of Truth\" but layered a specialized SaaS forecasting tool on top via API, getting 100% of the capability in a fraction of the time."
  },
  {
    id: "adaptive-learning-engine",
    ref_id: "CASE_06",
    title: "THE ADAPTIVE LEARNING ENGINE",
    category: "change-management",
    sector: "DIGITAL TRANSFORMATION / GOV",
    sensitivity: "PUBLIC_RELEASE",
    author_node: "SYDNEY",
    ppt_coverage: ["people", "process", "technology"],
    desc: "Moving from compliance reporting to active steering via real-time AI sentiment analysis.",
    date: "2025-03-01",
    read_time: "9 MIN READ",
    diagram_type: "real-time-dashboard",
    // Impact Radius Data
    impact_radius: {
      metric1: "CULTURE: SHIFTED",
      metric2: "VISIBILITY: REAL-TIME",
      metric3: "ALIGNMENT: 100%"
    },
    // Case Study Content
    executive_abstract: "FROM COMPLIANCE TO INNOVATION. In government, \"reporting\" usually means looking backward. We built a system that looks forward, using real-time data to steer the ship.",
    business_friction: "THE \"SET AND FORGET\" FAILURE. The consortium was managing a complex rollout using traditional methods—waiting for post-project reviews to find failures. \"Compliance-First\" culture was ticking boxes but missing the user experience.",
    intervention: "AI-DRIVEN STEERING. We embedded a \"Fractional Executive\" to bridge the gap. We replaced static reporting with an \"Executive Dashboard\" fed by real-time learner data and AI sentiment analysis, allowing leadership to pivot based on immediate feedback."
  },
  {
    id: "biosecurity-grid",
    ref_id: "CASE_07",
    title: "THE BIOSECURITY GRID",
    category: "computer-vision",
    sector: "COMPUTER VISION / GOVERNMENT",
    sensitivity: "PUBLIC_RELEASE",
    author_node: "SYDNEY",
    ppt_coverage: ["process", "technology"],
    desc: "Deploying a Multi-Agent Consensus grid to filter false-positive biosecurity threats in real-time.",
    date: "2025-03-10",
    read_time: "8 MIN READ",
    tech_stack: "Computer Vision, Multi-Agent Systems, Python",
    diagram_type: "biosecurity",
    // Impact Radius Data
    impact_radius: {
      metric1: "ACCURACY: >90%",
      metric2: "AGENTS: 3-LAYER",
      metric3: "RESPONSE: REAL-TIME"
    },
    // Case Study Content
    executive_abstract: "CROWDSOURCED DEFENSE. Government hotlines were overwhelmed by false alarms. We replaced manual verification with a \"Multi-Agent\" AI grid that thinks like a team of scientists.",
    business_friction: "THE FALSE POSITIVE FLOOD. The public was reporting \"native ants\" as \"invasive pests,\" drowning the experts in noise. Manual verification was too slow to prevent an ecological breach. The system needed to filter noise at the edge, not at the call center.",
    intervention: "MULTI-AGENT CONSENSUS. We didn't just build one model; we built three. A \"Colorist\" (analyzing hue), a \"Morphologist\" (measuring body structure), and an \"Ecologist\" (analyzing soil context). These three AI agents vote on every image. Only if they reach a 2/3 consensus does the alert escalate to a human.",
    // System Configuration
    system_config: {
      "Core_Architecture": "AWS Lambda (Serverless)",
      "AI_Model": "Amazon Bedrock / Custom CV Models",
      "Image_Processing": "OpenCV + TensorFlow",
      "Security": "Government-Grade Encryption",
      "Response_Time": "<2s per image"
    }
  },
  {
    id: "vectorized-curriculum",
    ref_id: "CASE_08",
    title: "THE VECTORIZED CURRICULUM",
    category: "generative-ai",
    sector: "GENERATIVE AI / EDUCATION",
    sensitivity: "PUBLIC_RELEASE",
    author_node: "VIETNAM",
    ppt_coverage: ["process", "technology"],
    desc: "Scaling elite pedagogy via RAG (Retrieval-Augmented Generation) and vector search pipelines.",
    date: "2025-03-15",
    read_time: "7 MIN READ",
    tech_stack: "RAG Pipeline, Vector Database, LLMs",
    diagram_type: "rag-pipeline",
    // Impact Radius Data
    impact_radius: {
      metric1: "SCALE: 1:1000",
      metric2: "LATENCY: <200MS",
      metric3: "FAIL-RATE: 0%"
    },
    // Case Study Content
    executive_abstract: "THE INFINITE TEACHER. Rural schools lacked the technical staff to debug student code. We built a RAG pipeline that turns every error message into a personalized \"Micro-Lesson.\"",
    business_friction: "THE DEBUGGING BOTTLENECK. Generalist teachers lost confidence when students hit complex syntax errors, causing lessons to stall. The school needed to scale their elite curriculum without cloning their Master Teachers.",
    intervention: "RETRIEVAL-AUGMENTED GENERATION. We converted the entire curriculum and codebase into data vectors. Now, when a student hits an error, the AI analyzes the syntax, \"retrieves\" the specific explanation from the Master Teacher's library, and serves a fix-it video in real-time. It is self-correction at scale.",
    // System Configuration
    system_config: {
      "Core_Architecture": "Microservices (Docker/K8s)",
      "AI_Model": "OpenAI GPT-4 + Embeddings",
      "Vector_Database": "Pinecone / Weaviate",
      "Frontend": "React / Next.js",
      "Latency_Target": "<200ms"
    }
  },
  {
    id: "predictive-safety-net",
    ref_id: "CASE_09",
    title: "THE PREDICTIVE SAFETY NET",
    category: "predictive-analytics",
    sector: "PREDICTIVE ANALYTICS / AGED CARE",
    sensitivity: "PUBLIC_RELEASE",
    author_node: "SYDNEY",
    ppt_coverage: ["people", "process", "technology"],
    desc: "From reaction to prevention: AI-powered fall prediction reducing serious incidents by 40%.",
    date: "2025-03-20",
    read_time: "8 MIN READ",
    tech_stack: "Wearable Telemetry, Predictive Modeling, EHR Integration",
    visual_emphasis: true,
    diagram_type: "predictive-analytics",
    // Impact Radius Data
    impact_radius: {
      metric1: "SAVINGS: $1.2M",
      metric2: "FALLS: -40%",
      metric3: "ACCURACY: 85%"
    },
    // Visual Data Points for Enhanced Display
    visual_metrics: [
      { label: "Annual Savings", value: "$1.2M", icon: "dollar" },
      { label: "Serious Incidents Reduced", value: "40%", icon: "trending-down" },
      { label: "Prediction Accuracy", value: "85%", icon: "target" },
      { label: "Residents Monitored", value: "5,000", icon: "users" }
    ],
    // Case Study Content
    executive_abstract: "FROM REACTION TO PREVENTION. A 5,000-resident provider was struggling with falls—the leading cause of hospital readmissions. We moved them from \"post-fall incident reporting\" to \"pre-fall intervention.\"",
    business_friction: "THE INVISIBLE RISK. Human staff cannot watch every resident 24/7. Subtle decline indicators—like micro-changes in gait, sleep disturbances, or heart rate fluctuations—were going unnoticed until a fall occurred. The cost of reactive care was reaching $1.2M in preventable injuries.",
    intervention: "THE DIGITAL TWIN. We implemented an AI-powered prediction engine that integrated real-time data from wearables and environmental monitors. The AI created a \"Digital Twin\" of the resident's health baseline. It now flags specific \"Pre-Fall\" patterns with 85% accuracy, triggering an alert to care managers before the incident happens.",
    // System Configuration
    system_config: {
      "Core_Architecture": "AWS IoT Core + Lambda",
      "AI_Model": "Predictive ML (Random Forest + LSTM)",
      "Data_Integration": "HL7 FHIR / EHR Connectors",
      "Hardware": "Wearable Sensors (Heart Rate, Accel.)",
      "Alert_System": "Real-Time Push Notifications"
    }
  },
  {
    id: "cognitive-bridge",
    ref_id: "CASE_10",
    title: "THE COGNITIVE BRIDGE",
    category: "change-management",
    sector: "NLP / AGED CARE",
    sensitivity: "PUBLIC_RELEASE",
    author_node: "VIETNAM",
    ppt_coverage: ["people", "technology"],
    desc: "Multilingual AI intake agent ensuring informed consent is never lost in translation.",
    date: "2025-03-25",
    read_time: "6 MIN READ",
    tech_stack: "Natural Language Processing (NLP), Real-Time Translation, Digital Agents",
    visual_emphasis: true,
    diagram_type: "nlp-translation",
    // Impact Radius Data
    impact_radius: {
      metric1: "DELAYS: ELIMINATED",
      metric2: "CONSENT: 100% AUDITABLE",
      metric3: "LANGUAGES: INSTANT"
    },
    // Visual Data Points for Enhanced Display
    visual_metrics: [
      { label: "Onboarding Delays", value: "0", icon: "clock" },
      { label: "Consent Audit Trail", value: "100%", icon: "shield-check" },
      { label: "Languages Supported", value: "Real-time", icon: "languages" },
      { label: "Clinical Accuracy", value: "High", icon: "check-circle" }
    ],
    // Case Study Content
    executive_abstract: "EQUITY IN ADMISSION. Language barriers in Aged Care are not just an inconvenience; they are a clinical liability. We deployed an AI Intake Agent to ensure \"Informed Consent\" is never lost in translation.",
    business_friction: "THE LANGUAGE GAP. Onboarding new clients who speak Mandarin, Arabic, or Greek was causing massive delays. Relying on family members to translate led to misunderstandings about care plans and medication, creating a significant governance risk regarding valid consent.",
    intervention: "THE AI INTAKE AGENT. We deployed a conversational AI agent capable of conducting clinical intake interviews in the resident's native language. It collects the necessary onboarding data and simultaneously translates it into English for the clinical staff. Crucially, it generates a verbatim audit log of the conversation, proving that informed consent was achieved."
  },
  {
    id: "workforce-optimizer",
    ref_id: "CASE_11",
    title: "THE WORKFORCE OPTIMIZER",
    category: "project-management",
    sector: "OPTIMIZATION / AGED CARE",
    sensitivity: "PUBLIC_RELEASE",
    author_node: "NEW_JERSEY",
    ppt_coverage: ["people", "process", "technology"],
    desc: "AI-powered scheduling optimization prioritizing continuity of care over slot-filling.",
    date: "2025-04-01",
    read_time: "7 MIN READ",
    tech_stack: "Constraint Satisfaction Algorithms, Geospatial Analytics",
    visual_emphasis: true,
    diagram_type: "workforce-optimization",
    // Impact Radius Data
    impact_radius: {
      metric1: "BURNOUT: REDUCED",
      metric2: "CONTINUITY: IMPROVED",
      metric3: "COMPLIANCE: 100%"
    },
    // Visual Data Points for Enhanced Display
    visual_metrics: [
      { label: "Staff Burnout", value: "Reduced", icon: "heart" },
      { label: "Care Continuity", value: "Optimized", icon: "repeat" },
      { label: "Compliance Rate", value: "100%", icon: "shield" },
      { label: "Variables Processed", value: "1000s", icon: "activity" }
    ],
    // Case Study Content
    executive_abstract: "FROM TETRIS TO STRATEGY. Rostering in Aged Care is a complex mathematical problem. We moved the client from manual \"slot-filling\" to an AI-driven model that prioritizes continuity of care.",
    business_friction: "THE SCHEDULING FRAGMENTATION. Manual rostering was creating inefficiencies. Coordinators struggled to balance complex variables: staff qualifications, resident preferences, and travel times between locations. This resulted in fragmented care, high travel costs, and staff burnout.",
    intervention: "MULTI-VARIABLE OPTIMIZATION. We deployed an AI engine that treats rostering as a logic puzzle. The system ingests thousands of variables—including historical performance patterns and compliance requirements. It automates the schedule to ensure the \"Best Fit\" carer is matched to the resident, optimizing for minimal travel time and maximum staff satisfaction."
  },
  {
    id: "case_12",
    ref_id: "CASE_12",
    title: "LIBERATING LEGAL EXPERTISE",
    category: "ai",
    sector: "AI / LEGAL SERVICES",
    sensitivity: "PUBLIC_RELEASE",
    author_node: "SYDNEY",
    ppt_coverage: ["people", "process", "technology"],
    desc: "How AI-powered workflows are saving NSW Workers' Compensation practices from administrative leakage and unlocking billable capacity across Sydney and Australia.",
    date: "2026-05-01",
    read_time: "8 MIN READ",
    tech_stack: "Real-Time AI Transcription, RAG over Medical Files, LEAP Integration, Workflow Automation",
    visual_emphasis: true,
    diagram_type: "legal-automation",
    tags: [
      "#NSWWorkersCompensation",
      "#LegalAI",
      "#LEAPIntegration",
      "#WorkflowAutomation",
      "#Australia",
      "#LegalTechSydney"
    ],
    // Impact Radius Data
    impact_radius: {
      metric1: "TRIAGE: -50%",
      metric2: "BILLABLE: +30%",
      metric3: "COGNITIVE LOAD: -25%"
    },
    // Visual Data Points for Enhanced Display
    visual_metrics: [
      { label: "Manual Triage Reduced", value: "50%", icon: "trending-down" },
      { label: "Billable Time Recaptured", value: "+30%", icon: "dollar" },
      { label: "Cognitive Load Drop", value: "25%", icon: "activity" },
      { label: "Admin Capacity", value: "2x", icon: "users" }
    ],
    // Case Study Content
    executive_abstract: "LIBERATING LEGAL EXPERTISE. For Australian law firms running NSW Workers' Compensation practices, the best legal minds are trapped in \"administrative leakage\" — searching 10,000-page medical files with nothing but Ctrl+F and manually chasing insurers for missing funds. We converted that manual burden into a governed, AI-powered \"Connected Practice\" — deployed across Sydney and NSW legal teams — so lawyers do law, not data entry.",
    business_friction: "THE PRODUCTIVITY PARADOX. The traditional model for NSW Workers' Compensation claims relies on manual document gathering and offshore administrative processing. Systems like LEAP are often in place, but frequently function as little more than digital filing cabinets. As the volume of unstructured medical data grows, firm margins compress under the weight of manual labour — capping billable capacity, eroding client empathy, and burning out senior staff.",
    intervention: "THE CONNECTED PRACTICE. We activate AI layers within the firm's existing LEAP infrastructure — no rip-and-replace — and deploy a four-stage workflow. (1) Intelligent Intake: real-time AI transcription captures client interviews so lawyers focus on empathy and rapport rather than note-taking. (2) Automated Admin: AI scans transcripts to instantly trigger medical record requests, doubling administrative capacity without increasing headcount. (3) Precision Research: returning records are ingested by AI to build date-sorted medical chronologies in minutes, not days — every claim linked back to the primary source for verification. (4) Dispute Acceleration: the system automatically cross-references insurer denials against medical files to produce first-draft dispute letters. The result is a governed, audit-ready system — not a scattered SaaS stack.",
    outcome: "In the 2026 Australian legal market, AI is no longer an edge case — it is the baseline for survival. DD Consulting bridges the \"Execution Gap\" so Sydney, NSW and national firms don't just subscribe to SaaS tools, they implement systems that scale: 50% reduction in manual triage, 25% drop in cognitive load and up to 30% more billable time recaptured from administrative leakage.",
    // System Configuration
    system_config: {
      "Core_Architecture": "AWS Sydney Region (ap-southeast-2)",
      "AI_Transcription": "Real-Time Clinical-Grade STT",
      "Document_AI": "RAG over Medical Files + LEAP Data",
      "Integration_Layer": "LEAP API + Insurer Portals",
      "Compliance": "APP + NSW Legal Professional Conduct Rules",
      "Audit_Trail": "Every AI output linked to primary source"
    }
  },
  {
    id: "case_13",
    ref_id: "CASE_13",
    title: "THE EXECUTION GAP",
    category: "ai",
    sector: "AI / LEGAL SERVICES",
    sensitivity: "PUBLIC_RELEASE",
    author_node: "SYDNEY",
    ppt_coverage: ["people", "process", "technology"],
    desc: "Why the 1950s billable model is dying — and how governed AI (ISO 42001) closes the Execution Gap for Australian law firms navigating Alternative Fee Arrangements.",
    date: "2026-05-02",
    read_time: "9 MIN READ",
    tech_stack: "Connected Enterprise AI, ISO 42001 Governance, RAG, Contract Review AI, Invoice Analytics",
    visual_emphasis: true,
    diagram_type: "legal-governance",
    tags: [
      "#AlternativeFeeArrangements",
      "#AFA",
      "#BillableLeakage",
      "#LegalAIGovernance",
      "#ISO42001",
      "#ContractReviewAI",
      "#LegalSynthesis",
      "#LawFirmROI",
      "#AustraliaLegalTech"
    ],
    // Impact Radius Data
    impact_radius: {
      metric1: "LEAKAGE: $27K/LAWYER",
      metric2: "RESEARCH: -90%",
      metric3: "GOVERNANCE: ISO 42001"
    },
    // Visual Data Points for Enhanced Display
    visual_metrics: [
      { label: "Manual Effort Reduced (Triage)", value: "50%", icon: "trending-down" },
      { label: "Research Cycle Time Reduction", value: "90%", icon: "zap" },
      { label: "Senior Associate Hours Reclaimed", value: "25/wk", icon: "clock" },
      { label: "Chronology Extraction Precision", value: "99.97%", icon: "target" },
      { label: "Invoice Audit Accuracy", value: "92%", icon: "shield-check" },
      { label: "Lawyer Wellbeing Uplift", value: "93%", icon: "heart" }
    ],
    // Case Study Content
    executive_abstract: "THE EXECUTION GAP. The 1950s billable structure is leaking revenue — an average of $27,000 per lawyer per year to uncaptured administrative tasks — while corporate counsel now demand Generative AI as a baseline requirement for panel selection. Alternative Fee Arrangements (AFAs) are accelerating the shift. Australian law firms that rely on manual labour to \"hunt\" for data will watch their margins disappear. DD Consulting closes this Execution Gap with a governed, ISO 42001-aligned Connected Enterprise — not another SaaS subscription.",
    business_friction: "THE BILLABLE LEAKAGE CRISIS. The traditional law firm model is under siege from two sides. First, corporate counsel now demand Generative AI capability as a baseline for selection. Second, the \"1950s Billable Structure\" is haemorrhaging revenue — roughly $27K in annual leakage per lawyer from uncaptured admin — at the exact moment clients are pushing for Alternative Fee Arrangements. Most \"flashy\" Proof of Concepts fail because they lack audit-ready controls and enterprise stability. Without governance, AI is just another liability.",
    intervention: "THE CONNECTED ENTERPRISE // ISO 42001 ALIGNED. DD Consulting deploys a governed AI architecture across five interventions that move from anecdote to measurable ROI: (1) Intelligent Triage — 50% reduction in manual effort. (2) Legal Synthesis — research cycle times slashed by up to 90%. (3) Contract Review — up to 25 hours per week reclaimed for senior associates. (4) Litigation Chronology — thousands of pages converted into verified timelines with 99.97% extraction precision, every claim linked to its source. (5) Autonomous Invoice Governance — 92% accuracy in billing audits, surpassing human auditors by 20%. The entire stack is aligned with the ISO 42001 AI management standard and deployed in the AWS Sydney Region, making it secure-by-design, audit-ready, and scalable.",
    outcome: "Beyond efficiency, the ROI is human. Our 2025 Neuro-Insight Study found that lawyers operating within tech-assisted workflows saw a 25% drop in cognitive load, and 93% of participants reported increased professional wellbeing — liberated from data gathering to return to high-level legal analysis. In 2026, the question is no longer IF Australian firms use AI, but HOW they govern it. DD Consulting is how law firm AI ROI gets engineered in Sydney and across Australia — not promised.",
    // System Configuration
    system_config: {
      "Governance_Standard": "ISO/IEC 42001:2023 (AI Management)",
      "Core_Architecture": "AWS Sydney Region (ap-southeast-2)",
      "Contract_AI": "RAG + Clause Library + LLM Review",
      "Invoice_AI": "Autonomous Billing Audit Engine",
      "Chronology_Engine": "99.97% Extraction Precision",
      "Compliance": "APP + NSW Legal Professional Conduct Rules",
      "Audit_Trail": "Every AI output traceable to primary source"
    }
  },
  {
    id: "case_14",
    ref_id: "CASE_14",
    title: "THE ROI OF GOVERNANCE",
    category: "governance",
    sector: "AI GOVERNANCE / ENTERPRISE STRATEGY",
    sensitivity: "PUBLIC_RELEASE",
    author_node: "SYDNEY",
    ppt_coverage: ["people", "process", "technology"],
    desc: "Why engineering secure-by-design AI — aligned to ISO 42001 — is the only path to scale for Australian enterprises managing high-stakes assets.",
    date: "2026-05-03",
    read_time: "9 MIN READ",
    tech_stack: "ISO 42001 AIMS, Secure-by-Design Architecture, AWS, Google Cloud, Technical Program Management",
    visual_emphasis: true,
    diagram_type: "governance-architecture",
    tags: [
      "#ISO42001",
      "#AIGovernance",
      "#BuildVsBuy",
      "#SecureByDesign",
      "#AWS",
      "#GoogleCloud",
      "#TechnicalProgramManagement",
      "#CIO",
      "#CTO",
      "#Australia",
      "#EnterpriseAI"
    ],
    // Impact Radius Data
    impact_radius: {
      metric1: "ASSETS MANAGED: MULTI-MILLION-DOLLAR PORTFOLIO",
      metric2: "TIERS: 3 (CIO/CTO/SME)",
      metric3: "STANDARD: ISO 42001"
    },
    // Visual Data Points for Enhanced Display
    visual_metrics: [
      { label: "Technical Assets Governed", value: "Multi-million", icon: "dollar" },
      { label: "Architecture Tiers", value: "3", icon: "layers" },
      { label: "Governance Standard", value: "ISO 42001", icon: "shield-check" },
      { label: "Cloud Platforms", value: "AWS + GCP", icon: "cloud" }
    ],
    // Case Study Content
    executive_abstract: "THE ROI OF GOVERNANCE. In a market saturated with AI hype, the distinction between a successful deployment and a failed pilot comes down to one factor: Technical Governance. For Australian enterprises managing high-stakes assets — across healthcare, legal, financial services and regulated operations — the 'Build vs. Buy' dilemma is no longer just about cost. It's about auditability, security, and long-term scalability. DD Consulting engineers secure-by-design AI architectures — aligned to ISO 42001 — that serve the P&L, not the other way around.",
    business_friction: "THE CIO / CTO / SME GAP. Most Australian organisations suffer from a three-way disconnect: strategy at the CIO level, execution at the CTO level, and daily operation at the SME level. Each tier speaks a different language. Without a governance architecture that spans all three, AI initiatives stall: flashy proofs-of-concept that never reach production, or tools that are deployed but never adopted. The result is stranded capital, eroded trust with auditors and partners, and an enterprise AI stack that is neither secure-by-design nor audit-ready.",
    intervention: "THE 3-TIER ARCHITECTURE. DD Consulting bridges the Execution Gap with a three-tier engagement model. (1) STRATEGIC AUDIT — The CIO Level: rigorous Build vs. Buy assessments that ensure the tech stack serves the business P&L, with a clear governance spine aligned to ISO 42001. (2) SCALABLE INFRASTRUCTURE — The CTO Level: robust, secure-by-design backends on AWS (Sydney Region) and Google Cloud, architected for the demands of 2026 and beyond. (3) TECHNICAL PROGRAM MANAGEMENT — The Delivery Level: strategy fails without adoption, so we drive the Business-as-Usual transition end-to-end — ensuring your teams actually use what was built. One governance spine. Three execution tiers. Zero orphaned pilots.",
    outcome: "ISO 42001 IS YOUR COMPETITIVE ADVANTAGE. By aligning your Artificial Intelligence Management System with ISO 42001, you move from 'checkbox compliance' to a provable Trust Layer — a signal to clients, regulators and partners that your AI is governed, ethical and audit-ready. DD Consulting currently governs a multi-million-dollar portfolio of technical assets across Australian enterprises under this exact model. In 2026, this is no longer a differentiator — it is the baseline for scale.",
    // System Configuration
    system_config: {
      "Governance_Standard": "ISO/IEC 42001:2023 (AIMS)",
      "Strategy_Layer": "CIO — Build vs. Buy + P&L Alignment",
      "Architecture_Layer": "CTO — AWS Sydney + Google Cloud",
      "Execution_Layer": "SME — Technical Program Management",
      "Design_Principle": "Secure-by-Design, Audit-Ready",
      "Assets_Under_Governance": "Multi-million-dollar technical asset portfolio"
    }
  },
  {
    id: "case_15",
    ref_id: "CASE_15",
    title: "FROM CURIOSITY TO ASSURANCE",
    category: "governance",
    sector: "AI ASSURANCE / REGULATED INDUSTRIES",
    sensitivity: "PUBLIC_RELEASE",
    author_node: "SYDNEY",
    ppt_coverage: ["people", "process", "technology"],
    desc: "A 5-pillar AI Strategy & Assurance Framework (ISO 42001-aligned) for Australian healthcare, aged care and legal enterprises.",
    date: "2026-05-04",
    read_time: "10 MIN READ",
    tech_stack: "ISO 42001 AIMS, AI POC Accelerator, AI Control Register, Assurance Review Framework",
    visual_emphasis: true,
    diagram_type: "assurance-framework",
    tags: [
      "#ISO42001",
      "#AIMS",
      "#AIAssurance",
      "#DNV",
      "#PECB",
      "#HealthcareAI",
      "#AgedCare",
      "#LegalAI",
      "#AIPOC",
      "#AIStrategySprint",
      "#Australia",
      "#Governance"
    ],
    // Impact Radius Data
    impact_radius: {
      metric1: "PILLARS: 5",
      metric2: "POC CYCLE: 4-12 WEEKS",
      metric3: "STANDARD: ISO 42001 / AIMS"
    },
    // Visual Data Points for Enhanced Display
    visual_metrics: [
      { label: "Framework Pillars", value: "5", icon: "layers" },
      { label: "POC Accelerator Cycle", value: "4–12 wks", icon: "clock" },
      { label: "AIMS Standard", value: "ISO 42001", icon: "shield-check" },
      { label: "Target Sectors", value: "Health / Aged / Legal", icon: "users" },
      { label: "Cert Bodies Aligned", value: "DNV + PECB", icon: "check-circle" },
      { label: "Control Register", value: "Evidence-Ready", icon: "file-text" }
    ],
    // Case Study Content
    executive_abstract: "FROM CURIOSITY TO ASSURANCE. In the early days of generative AI, the question for most Australian enterprises was 'what can we do with this?'. In 2026, that question has been replaced by a far more urgent one: 'how do we deploy AI safely, accountably and in alignment with global standards like ISO 42001?'. For high-stakes industries — Healthcare, Aged Care and Legal Practices — 'move fast and break things' is not an option. The transition from AI curiosity to AI Assurance requires a documented architecture of evidence that satisfies regulators, auditors and stakeholders.",
    business_friction: "THE ASSURANCE VACUUM. Most Australian enterprises entering regulated AI deployment hit the same wall: strategy decks without execution, POCs without governance, and pilots without evidence. Global certification bodies like DNV and PECB are mobilising ISO 42001 Lead Auditors because compliant AI Management Systems (AIMS) are becoming a market necessity — yet most firms have no intake workflow, no AI control register, and no documented decision rights. Without a framework, 'AI curiosity' becomes a compounding liability.",
    intervention: "THE 5-PILLAR AI STRATEGY & ASSURANCE FRAMEWORK. DD Consulting bridges the Execution Gap through a rigorous five-stage process engineered to scale innovation without sacrificing governance. (1) DIGITAL TRANSFORMATION STRATEGY SPRINT — moving past vague roadmaps to a prioritised use-case portfolio and an ISO 42001 readiness heatmap. (2) GOVERNANCE & ISO 42001 OPERATING MODEL — not just policy documents, but the full system of management: intake workflows, decision rights, internal audit checkpoints. (3) DISCOVERY & ASSURANCE SCREENING — stopping flashy-but-poorly-controlled pilots by mapping every idea against a value-vs-feasibility matrix. (4) TEAM AI POC ACCELERATOR — measurable pilots (4–12 weeks) that generate an AI control register and evidence pack for broader rollout. (5) ASSURANCE & SCALE PARTNER — ongoing internal assurance reviews and remediation roadmaps so your AIMS stays audit-ready.",
    outcome: "THE ISO 42001 ADVANTAGE. By establishing clear decision rights and systematically mitigating AI-specific threats, your organisation moves from a position of risk to a position of competitive advantage. With DNV and PECB actively certifying AIMS across the Australian and global markets, a documented, audit-ready AI Management System is the new table stakes for regulated sectors — and a pricing lever for every other sector. DD Consulting is how Australian enterprises cross from 'we're exploring AI' to 'we're certified, governed and scaling'.",
    // System Configuration
    system_config: {
      "Framework": "5-Pillar AI Strategy & Assurance",
      "Governance_Standard": "ISO/IEC 42001:2023 (AIMS)",
      "Certification_Alignment": "DNV + PECB Lead Auditor ready",
      "POC_Cycle": "4-12 Weeks (measurable, evidence-packed)",
      "Control_Register": "AI-specific threats + decision rights",
      "Target_Sectors": "Healthcare, Aged Care, Legal (Australia)"
    }
  },
  {
    id: "case_16",
    ref_id: "CASE_16",
    title: "DECODING MICRO-MARGINS",
    category: "ai",
    sector: "SPORTS PERFORMANCE / ELITE ATHLETICS",
    sensitivity: "PUBLIC_RELEASE",
    author_node: "SYDNEY",
    ppt_coverage: ["people", "process", "technology"],
    desc: "How agentic AI, computer vision and edge compute are engineering micro-margin advantage for elite Rugby Sevens — tactical, adjudication, and athlete welfare.",
    date: "2026-05-05",
    read_time: "9 MIN READ",
    tech_stack: "Computer Vision, Agentic AI, Edge Compute, RAG over World Rugby Laws, MEMS Telemetry, UWB Communications",
    visual_emphasis: true,
    diagram_type: "sports-edge-ai",
    tags: [
      "#RugbySevens",
      "#SportsAI",
      "#ComputerVision",
      "#AgenticAI",
      "#EdgeComputing",
      "#WorldRugby",
      "#RAG",
      "#AthleteWelfare",
      "#MEMSTelemetry",
      "#ISO42001",
      "#Australia"
    ],
    // Impact Radius Data
    impact_radius: {
      metric1: "VIDEO SCRUB: -15 HRS",
      metric2: "INJURY PREDICT: 92%",
      metric3: "LATENCY: EDGE / ZERO"
    },
    // Visual Data Points for Enhanced Display
    visual_metrics: [
      { label: "Manual Video Scrub Eliminated", value: "15 hrs", icon: "trending-down" },
      { label: "Soft-Tissue Injury Prediction", value: "92%", icon: "activity" },
      { label: "Stadium Network Latency", value: "Edge / Zero", icon: "zap" },
      { label: "Technical Assets Governed", value: "Multi-million", icon: "dollar" },
      { label: "Governance Standard", value: "ISO 42001", icon: "shield-check" },
      { label: "Core Vectors", value: "3", icon: "layers" }
    ],
    // Case Study Content
    executive_abstract: "DECODING MICRO-MARGINS. In the fastest-moving code in world sport, victory is measured in milliseconds. As elite Rugby Sevens moves to a high-stakes, three-division model, coaches and medics are hitting a human limit. DD Consulting bridges that gap by deploying an Autonomous Sevens Ecosystem that transforms raw match data into immediate, actionable intelligence across tactics, adjudication and athlete welfare — all engineered under ISO 42001-aligned governance in the AWS Sydney Region.",
    business_friction: "THE HUMAN LIMIT. Coaches scrub 15+ hours of match footage per week to surface micro-trends that decide single-point games. Medics react to injury rather than forecast it. Referees' interpretations of World Rugby Laws are opaque to multi-lingual squads. And volatile stadium Wi-Fi makes every cloud-dependent analytics workflow a liability at kickoff. Without an engineered edge, world-class programs lose the margin they've trained years to earn.",
    intervention: "THE TRIPLE-VECTOR AUTONOMOUS SEVENS ECOSYSTEM. (1) TACTICAL INTELLIGENCE — Computer Vision eradicates the 15-hour manual video scrub by detecting micro-trends (e.g. defensive gaps opening post-phase three) and auto-generating synthetic training scenarios against those patterns. (2) RULE ADJUDICATION & GLOBAL ALIGNMENT — domain-adaptive LLMs grounded in World Rugby Laws via RAG identify 'infringement signatures' and translate referee interpretations into a player's native tongue in real time. (3) PREDICTIVE ATHLETE WELFARE — the AI Medic fuses skeletal tracking with MEMS mouthguard telemetry to monitor high-G impacts, forecasting soft-tissue injuries at 92% accuracy BEFORE they occur.",
    outcome: "EDGE-BASED PROCESSING, TOURNAMENT-GRADE. To bypass volatile stadium Wi-Fi, we deploy Ultra-Wideband communications and edge servers for zero-latency event detection — no cloud dependency at kickoff. This is high-performance engineering under governance: a multi-million-dollar technical asset portfolio managed, ISO 42001-aligned controls, and an Australian-authored AI architecture ready to travel with the squad.",
    // System Configuration
    system_config: {
      "Core_Architecture": "AWS Sydney Region (ap-southeast-2) + Tournament Edge Nodes",
      "Vision_Engine": "Computer Vision + Synthetic Scenario Generation",
      "Rules_Engine": "Domain-Adaptive LLM + RAG over World Rugby Laws",
      "Athlete_Welfare": "Skeletal Tracking + MEMS Mouthguard Telemetry",
      "On-Site_Link": "Ultra-Wideband (UWB) + Edge Servers (zero-latency)",
      "Governance": "ISO/IEC 42001:2023 Aligned"
    }
  },
  {
    id: "case_17",
    ref_id: "CASE_17",
    title: "BEYOND PILOT PURGATORY",
    category: "ai",
    sector: "FOOD & BEVERAGE / SMART MANUFACTURING",
    sensitivity: "PUBLIC_RELEASE",
    author_node: "SYDNEY",
    ppt_coverage: ["people", "process", "technology"],
    desc: "From Pilot Purgatory to Applied Intelligence — how demand-sensing, Vision AI and predictive maintenance compress F&B margins and drive 12–18 month payback.",
    date: "2026-05-06",
    read_time: "9 MIN READ",
    tech_stack: "LSTM + XGBoost Demand Sensing, Baumer VeriSens® Vision AI, Tractian Smart Trac Ultra Telemetry, MLOps",
    visual_emphasis: true,
    diagram_type: "industry-5-manufacturing",
    tags: [
      "#Industry5",
      "#FoodAndBeverage",
      "#DemandSensing",
      "#LSTM",
      "#XGBoost",
      "#VisionAI",
      "#QualityControl",
      "#PredictiveMaintenance",
      "#Baumer",
      "#Tractian",
      "#Jiva",
      "#Australia"
    ],
    // Impact Radius Data
    impact_radius: {
      metric1: "SAFETY STOCK: -18.8%",
      metric2: "MAPE: -32.8%",
      metric3: "DOWNTIME: -70%"
    },
    // Visual Data Points for Enhanced Display
    visual_metrics: [
      { label: "Safety Stock Reduction", value: "18.8%", icon: "trending-down" },
      { label: "Forecasting Error (MAPE) Reduction", value: "32.8%", icon: "activity" },
      { label: "Mechanical Downtime Drop", value: "70%", icon: "zap" },
      { label: "Seal Defect Detection", value: "0.3mm", icon: "target" },
      { label: "Vision AI Line Speed", value: "300 u/min", icon: "cpu" },
      { label: "Payback Window", value: "12–18 mo", icon: "clock" }
    ],
    // Case Study Content
    executive_abstract: "BEYOND PILOT PURGATORY. The Food & Beverage industry is moving from 'Pilot Purgatory' to Applied Intelligence. For brands like Jiva, a time-sensitive window exists to establish first-mover advantage before market saturation. DD Consulting bridges the Execution Gap by integrating AI across the entire value chain — from raw ingredients to consumer community — with measurable margin compression and a 12–18 month payback window.",
    business_friction: "THE OPERATIONAL MARGIN TRAP. F&B operators are drowning in stranded pilots: slick demand-sensing POCs that never hit the ERP; Vision AI demos that can't keep up with line speed; predictive maintenance dashboards nobody actions. Forecasting errors drive safety-stock bloat; micro-defects missed on the line become recalls; rotating-equipment failure wipes out entire production shifts. Margin compression is a symptom; the root cause is an un-governed, un-integrated AI stack.",
    intervention: "APPLIED INTELLIGENCE ACROSS THE VALUE CHAIN. (1) DEMAND-SENSING AI using LSTM + XGBoost models delivers 18.8% reduction in safety stock and 32.8% lower forecasting error (MAPE). (2) VISION AI QUALITY CONTROL using Baumer VeriSens® cameras detects 0.3mm seal defects — invisible to human operators — at speeds of 300 units / minute, preventing catastrophic recalls. (3) PREDICTIVE INFRASTRUCTURE via Tractian Smart Trac Ultra sensors on rotating equipment flags mechanical imbalances 2–8 weeks prior to failure, delivering a 70% reduction in mechanical downtime.",
    outcome: "ROI, NOT RHETORIC. We don't just build software; we engineer the full value chain for a 12–18 month payback period. Demand sensing, line-side Vision AI and predictive maintenance compound into a single operating system — governed, integrated and owned by the client. For Australian F&B brands, that is the compressed window between 'exploring AI' and 'defending margin at scale'.",
    // System Configuration
    system_config: {
      "Demand_AI": "LSTM + XGBoost ensemble forecasting",
      "Vision_AI": "Baumer VeriSens® (0.3mm seal-defect detection @ 300 u/min)",
      "Telemetry": "Tractian Smart Trac Ultra on rotating equipment",
      "Lead_Time_To_Failure": "2–8 weeks early-warning",
      "Integration_Layer": "ERP + MES + MLOps pipelines",
      "Payback_Window": "12–18 months"
    }
  },
  {
    id: "case_18",
    ref_id: "CASE_18",
    title: "COMPRESSING THE TIME TO YES",
    category: "ai",
    sector: "FINANCIAL SERVICES / COMMERCIAL LENDING",
    sensitivity: "PUBLIC_RELEASE",
    author_node: "SYDNEY",
    ppt_coverage: ["people", "process", "technology"],
    desc: "Project Velocity's Entity Brain compresses fund evaluation from 10 days to 1 day — 3x admin capacity, 100% compliance mapping, Basiq + Equifax + PEXA integrated.",
    date: "2026-05-07",
    read_time: "8 MIN READ",
    tech_stack: "Entity Brain Architecture, Basiq + Equifax + PEXA Integrations, Automated Trust Deed Review, ICR/DSR Calculation Engine",
    visual_emphasis: true,
    diagram_type: "fintech-entity-brain",
    isFAQ: true,
    tags: [
      "#CommercialLending",
      "#FundEvaluation",
      "#TrustDeedAutomation",
      "#Basiq",
      "#Equifax",
      "#PEXA",
      "#ICRDSR",
      "#EntityBrain",
      "#ProjectVelocity",
      "#FintechAustralia",
      "#Australia"
    ],
    // Impact Radius Data
    impact_radius: {
      metric1: "TIME TO YES: 10d → 1d",
      metric2: "ADMIN CAPACITY: 3x",
      metric3: "COMPLIANCE MAPPING: 100%"
    },
    // Visual Data Points for Enhanced Display
    visual_metrics: [
      { label: "Fund Evaluation Cycle", value: "10d → 1d", icon: "clock" },
      { label: "Administrative Capacity Uplift", value: "3x", icon: "users" },
      { label: "Compliance Mapping Rate", value: "100%", icon: "shield-check" },
      { label: "Trust Deed Review (was)", value: "2–4 hrs", icon: "trending-down" },
      { label: "Live Data Integrations", value: "Basiq + Equifax + PEXA", icon: "cpu" },
      { label: "Core Metric Engine", value: "ICR / DSR", icon: "target" }
    ],
    // FAQ Content (renders visible Direct Answer block + FAQPage JSON-LD)
    faq_sections: [
      {
        question: "How does DD Consulting accelerate commercial lending and financial audits?",
        answer: "Through Project Velocity, DD Consulting uses an \"Entity Brain\" architecture to compress fund evaluations from 10 days down to 1 day. By automating manual trust deed reviews and integrating with financial data providers like Basiq and Equifax, firms achieve 3x administrative capacity and a 100% compliance mapping rate for complex entities."
      },
      {
        question: "What is the \"Entity Brain\" in Project Velocity?",
        answer: "The Entity Brain is a unified representation of a borrower or fund — trust structures, beneficiaries, cashflows and encumbrances — that ingests live data from Basiq, Equifax and PEXA and calculates ICR and DSR metrics the instant data arrives, replacing manual, paper-based fund evaluation workflows."
      },
      {
        question: "Which financial data providers does Project Velocity integrate with?",
        answer: "Project Velocity integrates natively with Basiq (transaction and account data), Equifax (credit and identity data) and PEXA (settlement and property data). Together these feeds hydrate the Entity Brain so lending analysts get an instant, audit-ready view of each commercial entity."
      }
    ],
    // Case Study Content
    executive_abstract: "COMPRESSING THE TIME TO YES. In commercial lending and fund evaluation, manual, paper-based processes are the silent killers of deal velocity. Traditional trust deed reviews take 2–4 hours per file; fund evaluation cycles stretch to 10 days. DD Consulting engineered Project Velocity — an Entity Brain architecture integrated with Basiq, Equifax and PEXA — to compress those timelines into a single day and triple administrative capacity without adding headcount.",
    business_friction: "THE PAPER-BASED BOTTLENECK. Commercial lending and fund evaluation teams in Australia still run on PDFs, trust deeds and spreadsheets. Analysts spend 2–4 hours per file hand-reading deed clauses; fund evaluations stretch to 10 days as data is re-keyed across Basiq, Equifax and PEXA extracts; and ICR / DSR calculations are recomputed manually for every entity variant. The business cost is opportunity cost — every day of delay is a deal the competitor closes first.",
    intervention: "THE ENTITY BRAIN. Project Velocity replaces the paper stack with a unified Entity Brain — a live, machine-readable representation of each borrower or fund that ingests transaction data (Basiq), credit and identity data (Equifax) and settlement and property data (PEXA) the moment it is received. Trust deed review is automated against a clause library; ICR and DSR are calculated instantly on ingestion; and complex trust structures are mapped to compliance rules deterministically. Analysts move from data-gathering to decision-making.",
    outcome: "MEASURED OUTCOMES. 10-day fund evaluations compressed to 1 day. 3x increase in administrative capacity without adding headcount. 100% compliance rate mapping against complex trust structures. For Australian commercial lenders and fund managers, Project Velocity turns 'Time to Yes' from a competitive liability into a deal-velocity moat.",
    // System Configuration
    system_config: {
      "Architecture": "Entity Brain — unified borrower / fund representation",
      "Data_Integrations": "Basiq + Equifax + PEXA (live ingestion)",
      "Automation_Layer": "Trust Deed Clause Library + Rules Engine",
      "Metric_Engine": "ICR / DSR calculated on ingestion",
      "Core_Architecture": "AWS Sydney Region (ap-southeast-2)",
      "Governance": "Audit-ready, ISO 42001-aligned controls"
    }
  }
];
